'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';


// ─── Types ───────────────────────────────────────────────────────────────────
interface PaymentAccount {
  id: string;
  logoUrl: string;
  logoAlt: string;
  bankName: string;
  fields: { label: string; value: string }[];
  titular: string;
}

interface RaffleCard {
  id: string;
  imageUrl: string;
  imageAlt: string;
  title: string;
  date: string;
  href: string;
  progress?: number;
  total?: number;
  actual?: number;
}

interface RaffleConfig {
  id: string;
  title: string;
  description: string;
  image_url: string;
  image_alt: string;
  price: number;
  draw_date: string;
  total_tickets: number;
  sold_tickets: number;
  href: string;
  is_active: boolean;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const LOGO_URL =
  'https://rifarito.s3.amazonaws.com/uploads/client/logo/2570/LGmiganga_logo_05.png';

const BANNER_URL =
  'https://rifarito.s3.amazonaws.com/uploads/client/banner/2570/prdcamiganga_logo_05.jpg';

const RAFFLE_IMG_1 =
  'https://rifarito.s3.amazonaws.com/uploads/raffle/image/6245/1784423427885.jpg';

const RAFFLE_IMG_2 =
  'https://rifarito.s3.amazonaws.com/uploads/raffle/image/6242/1784167329332.jpg';

const PAGOMOVIL_LOGO =
  '/assets/images/image-1785507951211.png';

const BINANCE_LOGO =
  'https://rifarito.s3.amazonaws.com/uploads/bank/logo/283/ven-binance.png';

const RIFARITO_WATERMARK =
  'https://rifarito.s3.amazonaws.com/assets/rifaritowatermark-c.png';

const TIKTOK_LOGO =
  'https://rifarito.s3.amazonaws.com/uploads/client/logo/2570/LGmiganga_logo_05.png';

const availableRaffles: RaffleCard[] = [
  {
    id: 'combo-favorito',
    imageUrl: RAFFLE_IMG_1,
    imageAlt: 'Combo El Favorito de Todoos - rifa con premios de electrodomésticos y vehículo',
    title: 'COMBO EL FAVORITO DE TODOOS 🤩🔥🚘',
    date: '🗓️ 20 Julio, 2026',
    href: '/comboelfavoritodetodoos',
    progress: 13.3,
    total: 10000,
    actual: 1335,
  },
];

const finalizedRaffles: RaffleCard[] = [
  {
    id: 'combo-nuevo-rico',
    imageUrl: RAFFLE_IMG_2,
    imageAlt: 'Comboo Nuevo Rico - rifa finalizada con premios especiales',
    title: 'COMBOO NUEVO RICO 💎✨🤩',
    date: '18 Julio, 2026',
    href: '/comboonuevorico',
  },
];

const paymentAccounts: PaymentAccount[] = [
  {
    id: 'pagomovil',
    logoUrl: PAGOMOVIL_LOGO,
    logoAlt: 'Pago Móvil BVC logo',
    bankName: 'Banco Venezolano de Crédito',
    fields: [
      { label: 'Banco Venezolano de Crédito PAGO MÓVIL', value: '04262126049' },
      { label: 'CÉDULA', value: '27200898' },
      { label: 'BANCO VENEZOLANO DE CRÉDITO', value: '0104' },
    ],
    titular: 'Cristhian Camilo Ortiz',
  },
  {
    id: 'binance',
    logoUrl: BINANCE_LOGO,
    logoAlt: 'Binance logo',
    bankName: 'No disponible',
    fields: [],
    titular: '',
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      });
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className={`rc-copy-btn${copied ? ' copied' : ''}`}
      title="Copiar"
      aria-label="Copiar"
    >
      <i className={copied ? 'fa fa-check' : 'fa fa-files-o'} aria-hidden="true" />
    </button>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  const isOuter = percent < 12;
  return (
    <div className="rc-progress-contain">
      <div className="rc-progress-total" />
      <div className="rc-progress-actual" style={{ width: `${percent}%` }} />
      <div
        className={`rc-progress-percent${isOuter ? ' outer' : ''}`}
        style={isOuter ? { left: `${percent}%`, transform: 'translateX(4px)' } : { left: `${percent}%` }}
      >
        {percent}%
      </div>
    </div>
  );
}

function RaffleCardComponent({ card, showProgress }: { card: RaffleCard; showProgress?: boolean }) {
  return (
    <div className="rc-card-container">
      <div className="rc-card">
        <div className="rc-card-img-wrap">
          <img src={card.imageUrl} alt={card.imageAlt} style={{ width: '100%', borderRadius: '18px', height: 'auto' }} />
        </div>
        <h3 className="rc-card-title">{card.title}</h3>
        <h4 className="rc-card-subtitle">{card.date}</h4>
        {showProgress && card.progress !== undefined && (
          <ProgressBar percent={card.progress} />
        )}
        <a className="rc-btn-card" href={card.href}>
          {showProgress ? 'COMPRAR TICKETS' : 'Ver Evento'}
        </a>
      </div>
    </div>
  );
}

function PaymentCard({ account }: { account: PaymentAccount }) {
  return (
    <div className="rc-payment-card">
      <img
        src={account.logoUrl}
        alt={account.logoAlt}
        style={{ width: '60px', height: 'auto', borderRadius: '16px' }}
      />
      <p className="rc-payment-bank-name">{account.bankName}</p>
      {account.fields.map((field) => (
        <React.Fragment key={field.label}>
          <strong className="rc-payment-label">{field.label}</strong>
          <div className="rc-payment-value-row">
            <span className="rc-payment-value">{field.value}</span>
            <CopyButton text={field.value} />
          </div>
        </React.Fragment>
      ))}
      <div className="rc-payment-value-row" style={{ marginTop: '10px' }}>
        <span className="rc-payment-titular">
          <strong>Titular:</strong> {account.titular}
        </span>
        <CopyButton text={account.titular} />
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function HomePage() {
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [adminError, setAdminError] = useState('');
  const [raffleConfig, setRaffleConfig] = useState<RaffleConfig | null>(null);
  const [dynamicPaymentAccounts, setDynamicPaymentAccounts] = useState<PaymentAccount[]>(paymentAccounts);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    async function loadRaffleConfig() {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('raffle_config')
          .select('*')
          .eq('is_active', true)
          .limit(1)
          .single();
        if (data) {
          setRaffleConfig(data);
          // Load dynamic payment data if available
          if (data.payment_bank || data.payment_phone || data.payment_cedula) {
            const bankName = data.payment_bank || 'Banco Venezolano de Crédito';
            const phone = data.payment_phone || '04262126049';
            const cedula = data.payment_cedula || '27200898';
            const bankCode = data.payment_bank_code || '0104';
            const titular = data.payment_titular || 'Cristhian Camilo Ortiz';
            setDynamicPaymentAccounts([
              {
                id: 'pagomovil',
                logoUrl: PAGOMOVIL_LOGO,
                logoAlt: 'Pago Móvil BVC logo',
                bankName: bankName,
                fields: [
                  { label: `${bankName} PAGO MÓVIL`, value: phone },
                  { label: 'CÉDULA', value: cedula },
                  { label: bankName.toUpperCase(), value: bankCode },
                ],
                titular: titular,
              },
              {
                id: 'binance',
                logoUrl: BINANCE_LOGO,
                logoAlt: 'Binance logo',
                bankName: 'No disponible',
                fields: [],
                titular: '',
              },
            ]);
          }
        }
      } catch {
        // fallback to static data
      }
    }
    loadRaffleConfig();
  }, []);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const handleAdminLogin = () => {
    if (adminUser === 'admin' && adminPass === 'adminganga123*') {
      sessionStorage.setItem('admin_auth', 'true');
      setAdminLoggedIn(true);
      setAdminModalOpen(false);
      setAdminError('');
      router.push('/admin');
    } else {
      setAdminError('Usuario o contraseña incorrectos.');
    }
  };

  const dynamicRaffle: RaffleCard = raffleConfig
    ? {
        id: raffleConfig.id,
        imageUrl: raffleConfig.image_url,
        imageAlt: raffleConfig.image_alt,
        title: raffleConfig.title,
        date: raffleConfig.draw_date,
        href: raffleConfig.href,
        progress: parseFloat(((raffleConfig.sold_tickets / Math.max(1, raffleConfig.total_tickets)) * 100).toFixed(1)),
        total: raffleConfig.total_tickets,
        actual: raffleConfig.sold_tickets,
      }
    : availableRaffles[0];

  return (
    <>
      {/* ── NAVBAR ── */}
      <nav id="navbar" className={`rc-navbar${scrolled ? ' scrolled' : ''}`}>
        <div className="rc-navbar-container">
          {/* Logo */}
          <div className="rc-navbar-brand">
            <a href="#top">
              <img src={LOGO_URL} alt="Rifas Camiganga logo" style={{ width: '100px', height: 'auto' }} />
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="rc-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              color: '#948f30',
              cursor: 'pointer',
              padding: '8px',
            }}
          >
            <i className="fa fa-th-large" aria-hidden="true" />
          </button>

          {/* Nav links */}
          <div
            className={`rc-nav-collapse${mobileMenuOpen ? ' open' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              flex: 1,
              justifyContent: 'space-between',
              marginLeft: '20px',
            }}
          >
            <ul className="rc-nav-links">
              <li>
                <a href="#top" onClick={handleNavClick}>
                  INICIO
                </a>
              </li>
              <li>
                <a href="#pagos" onClick={handleNavClick}>
                  CUENTAS DE PAGO
                </a>
              </li>
              <li>
                <a href="#footer" onClick={handleNavClick}>
                  CONTACTO
                </a>
              </li>
            </ul>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              <li>
                <a className="rc-btn-eventos" href="#eventos" onClick={handleNavClick}>
                  Eventos
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* ── ANCHOR TOP ── */}
      <div id="top" />

      {/* ── HEADER / HERO ── */}
      <div className="rc-header">
        <div className="rc-header-container">
          {/* Banner image */}
          <div className="rc-header-img-section">
            <img
              src={BANNER_URL}
              alt="Camiganga mascot - personaje animado con gafas y dinero"
              style={{ width: '100%', height: 'auto', borderRadius: '32px' }}
            />
          </div>

          {/* Title section */}
          <div className="rc-header-title-section">
            <p className="rc-header-subtitle">Carabobo, Venezuela</p>
            <h1 className="rc-header-title">RIFAS CAMIGANGA</h1>
            <pre className="rc-header-pre">RIFAS CAMIGANGA</pre>
            <div style={{ marginTop: '20px' }}>
              <a className="rc-btn-disponibles" href={dynamicRaffle.href}>
                LISTA DE DISPONIBLES
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── ANCHOR EVENTOS ── */}
      <div id="eventos" />

      {/* ── DISPONIBLES SECTION ── */}
      <div className="rc-clients-section">
        <div className="rc-clients-container">
          <div className="rc-clients-title-section">
            <p className="rc-clients-subtitle">¡ Participa !</p>
            <h2 className="rc-clients-title">Disponibles</h2>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <RaffleCardComponent card={dynamicRaffle} showProgress />
          </div>
        </div>
      </div>

      {/* ── ANCHOR NEWS ── */}
      <div id="news" />

      {/* ── FINALIZADOS SECTION ── */}
      <div className="rc-blog-section">
        <div className="rc-blog-container">
          <div className="rc-blog-title-section">
            <h2 className="rc-blog-title">Finalizados</h2>
          </div>
          <div className="rc-blog-posts-section">
            {finalizedRaffles.map((card) => (
              <RaffleCardComponent key={card.id} card={card} showProgress={false} />
            ))}
          </div>

          {/* Pagination */}
          <nav aria-label="Page navigation">
            <ul className="rc-pagination">
              <li className="active">
                <span>1</span>
              </li>
              <li>
                <a href="/?page=2">2</a>
              </li>
              <li className="disabled">
                <span>...</span>
              </li>
              <li>
                <a href="/?page=222">222</a>
              </li>
              <li>
                <a href="/?page=2">&gt;</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* ── ANCHOR CONTACT ── */}
      <div id="contact" />

      {/* ── CUENTAS DE PAGO SECTION ── */}
      <div id="pagos" className="rc-contact-section">
        <div className="rc-contact-container">
          <div className="rc-contact-title-section">
            <h2 className="rc-contact-title">Cuentas de Pago</h2>
          </div>

          {/* Payment cards */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '20px',
              width: '100%',
              marginTop: '10px',
            }}
          >
            {dynamicPaymentAccounts.map((account) => (
              <PaymentCard key={account.id} account={account} />
            ))}
          </div>

          {/* WhatsApp button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <a
              className="rc-btn-whatsapp"
              href="https://api.whatsapp.com/send?phone=5804144713576"
              rel="nofollow noopener noreferrer"
              target="_blank"
            >
              <i className="fa fa-whatsapp" aria-hidden="true" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer id="footer" className="rc-footer">
        <div className="rc-footer-container">
          {/* Col 1 - Logo */}
          <div className="rc-footer-col">
            <h3 className="rc-footer-title">RIFAS CAMIGANGA</h3>
            <div className="rc-footer-logo">
              <img
                src={LOGO_URL}
                alt="Rifas Camiganga logo"
                style={{ width: '180px', height: 'auto', borderRadius: '18px' }}
              />
            </div>
          </div>

          {/* Col 2 - Nosotros */}
          <div className="rc-footer-col">
            <h3 className="rc-footer-title">Nosotros</h3>
            <pre className="rc-footer-pre">RIFAS CAMIGANGA</pre>
          </div>

          {/* Col 3 - Contacto */}
          <div className="rc-footer-col">
            <h3 className="rc-footer-title">Contacto</h3>
            <ul className="rc-footer-list">
              <li>
                <a
                  href="https://www.rifascamiganga.com"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  style={{ color: '#dbdbdb' }}
                >
                  www.rifascamiganga.com
                </a>
              </li>
              <li>Valencia, Carabobo Venezuela</li>
              <li>0414-4393801</li>
            </ul>
          </div>

          {/* Col 4 - Síguenos */}
          <div className="rc-footer-col">
            <h3 className="rc-footer-title">Síguenos</h3>
            <div className="rc-footer-social">
              <a
                href="https://www.instagram.com/camiganga10"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="Instagram"
              >
                <i className="fa fa-instagram" aria-hidden="true" />
              </a>
              <a
                href="https://www.tiktok.com/@camigana"
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label="TikTok"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3V0Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Footer credits */}
        <div className="rc-footer-credits">
          <a
            href="https://www.rifarito.com"
            target="_blank"
            title="Sistema de Rifas"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.7)' }}
          >
            <span style={{ fontSize: '13px', fontFamily: 'Poppins, sans-serif' }}>WEB PROGRAMADA POR</span>
            <img
              src={RIFARITO_WATERMARK}
              width={166}
              height={47}
              alt="Sistema de Rifas"
              style={{ height: '30px', width: 'auto' }}
            />
          </a>
          <p>
            <span>
              <a href="/legal/terms.html" target="_blank" rel="nofollow noopener noreferrer">
                Condiciones
              </a>
              {' | '}
              <a href="/legal/privacy.html" target="_blank" rel="nofollow noopener noreferrer">
                Privacidad
              </a>
              {' | '}
              <a href="/legal/disclaimer.html" target="_blank" rel="nofollow noopener noreferrer">
                Responsabilidad
              </a>
            </span>
            <br />
            © 2025 Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* ── FLOATING CHAT BUTTON ── */}
      <div>
        {/* Admin floating button */}
        <div
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '20px',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => {
              if (adminLoggedIn) {
                router.push('/admin');
              } else {
                setAdminUser('');
                setAdminPass('');
                setAdminError('');
                setAdminModalOpen(true);
              }
            }}
            style={{
              background: adminLoggedIn ? '#16a34a' : '#1e293b',
              color: '#fff',
              border: 'none',
              borderRadius: '50px',
              padding: '10px 18px',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            aria-label="Admin"
          >
            <i className="fa fa-user-secret" aria-hidden="true" />
            {adminLoggedIn ? 'Admin ✓' : 'Admin'}
          </button>
        </div>

        {/* WhatsApp Channel floating button */}
        <a
          href="https://www.whatsapp.com/channel/0029VbDKMnT2UPBDxm2mbD2W"
          target="_blank"
          rel="nofollow noopener noreferrer"
          aria-label="Únete al canal de WhatsApp"
          style={{
            position: 'fixed',
            bottom: '75px',
            right: '10px',
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: '#25D366',
            color: '#fff',
            textDecoration: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#1ebe5d')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#25D366')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            width="22"
            height="22"
            fill="#fff"
            aria-hidden="true"
          >
            <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.469 2.027 7.77L0 32l8.437-2.007A15.94 15.94 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.77-1.848l-.485-.287-5.007 1.192 1.215-4.876-.317-.5A13.267 13.267 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.952c-.398-.199-2.355-1.162-2.72-1.294-.365-.133-.63-.199-.896.199-.265.398-1.029 1.294-1.261 1.56-.232.265-.465.298-.863.1-.398-.199-1.681-.62-3.202-1.977-1.183-1.057-1.982-2.362-2.214-2.76-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.664.133-.265.066-.497-.033-.697-.1-.199-.896-2.16-1.228-2.958-.323-.776-.651-.671-.896-.683l-.763-.013c-.265 0-.697.1-1.062.497-.365.398-1.394 1.362-1.394 3.322s1.427 3.853 1.626 4.119c.199.265 2.808 4.287 6.803 6.013.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.766-.114 2.355-.963 2.688-1.893.332-.93.332-1.727.232-1.893-.099-.166-.365-.265-.763-.464z" />
          </svg>
          <span
            style={{
              fontSize: '8px',
              fontWeight: 700,
              color: '#fff',
              marginTop: '1px',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            Únete
          </span>
        </a>

        <div
          id="buttfixed"
          className="rc-chat-btn"
          onClick={() => setChatOpen(!chatOpen)}
          role="button"
          tabIndex={0}
          aria-label="Chat"
          onKeyDown={(e) => e.key === 'Enter' && setChatOpen(!chatOpen)}
        >
          <i className="fa fa-commenting" aria-hidden="true" />
          <span>Chat</span>
        </div>
        <div id="linksfixed" className={`rc-chat-links${chatOpen ? ' active' : ''}`}>
          <a
            href="https://wa.me/+5804144713576"
            rel="nofollow noopener noreferrer"
            target="_blank"
            className="whatsapp"
            aria-label="WhatsApp"
          >
            <i className="fa fa-whatsapp" aria-hidden="true" />
          </a>
          <a
            href="https://t.me/camilortizexchange"
            rel="nofollow noopener noreferrer"
            target="_blank"
            className="telegram"
            aria-label="Telegram"
          >
            <i className="fa fa-telegram" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* ── ADMIN LOGIN MODAL ── */}
      {adminModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setAdminModalOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '32px 28px',
              width: '100%',
              maxWidth: '360px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setAdminModalOpen(false)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '14px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#64748b',
              }}
              aria-label="Cerrar"
            >
              ×
            </button>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>
              <i className="fa fa-lock" aria-hidden="true" style={{ marginRight: '8px' }} />
              Acceso Admin
            </h2>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Usuario
              </label>
              <input
                type="text"
                value={adminUser}
                onChange={(e) => setAdminUser(e.target.value)}
                placeholder="Usuario"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Contraseña
              </label>
              <input
                type="password"
                value={adminPass}
                onChange={(e) => setAdminPass(e.target.value)}
                placeholder="Contraseña"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdminLogin();
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            {adminError && (
              <p style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>
                {adminError}
              </p>
            )}
            <button
              onClick={handleAdminLogin}
              style={{
                width: '100%',
                background: '#1e293b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
              }}
            >
              Ingresar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
